import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Scissors, Plus, RefreshCw, Search, X, Check, MessageCircle, Phone,
  UserPlus, ChevronRight, Trash2,
} from "lucide-react";
import WhatsAppChat from "../../components/WhatsAppChat";
import { AtelierMedia, AtelierVideoSection } from "../../components/admin/AtelierMedia";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const digits = (p) => (p || "").replace(/[^\d]/g, "");
const waLink = (p) => `https://wa.me/${digits(p)}`;
const money = (v) => (v || v === 0 ? Number(v).toLocaleString("ru-RU") + " ₸" : "—");
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("ru-RU",
  { day: "2-digit", month: "2-digit", year: "2-digit" }) : "—");
const fmtDateTime = (iso) => (iso ? new Date(iso).toLocaleString("ru-RU",
  { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—");
const toIso = (v) => (v ? new Date(v).toISOString() : null);

const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export default function AdminAtelier() {
  const [meta, setMeta] = useState(null);
  const [managers, setManagers] = useState([]);
  const [data, setData] = useState({ items: [], counts: {} });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: "", manager_id: "", search: "" });
  const [openId, setOpenId] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [assignOpen, setAssignOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.status) params.status = filters.status;
      if (filters.manager_id) params.manager_id = filters.manager_id;
      if (filters.search) params.search = filters.search;
      const r = await axios.get(`${API}/admin/atelier/orders`,
        { params, withCredentials: true });
      setData({ items: r.data.items || [], counts: r.data.counts || {} });
      setSelected(new Set());
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/admin/atelier/meta`, { withCredentials: true }),
      axios.get(`${API}/admin/crm/managers`, { withCredentials: true }),
    ]).then(([m, mg]) => { setMeta(m.data); setManagers(mg.data || []); })
      .catch(console.error);
  }, []);

  useEffect(() => { if (meta) load(); /* eslint-disable-next-line */ },
    [meta, filters.status, filters.manager_id]);

  const total = useMemo(
    () => Object.values(data.counts || {}).reduce((a, b) => a + b, 0),
    [data.counts]);

  if (!meta) return <div style={{ padding: 40, textAlign: "center", color: "#999",
                                   fontFamily: "Manrope" }}>Загрузка воронки ателье…</div>;

  const canAssign = meta.can_assign;
  const toggleOne = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };
  const allSelected = data.items.length > 0 && data.items.every(o => selected.has(o.id));
  const toggleAll = () => setSelected(allSelected ? new Set()
                                                   : new Set(data.items.map(o => o.id)));

  return (
    <div data-testid="admin-atelier" style={{ paddingBottom: 32 }}>
      {/* Заголовок + номер приёма заявок */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
                     marginBottom: 16 }}>
        <Scissors size={18} color="#C5A059" />
        <div style={{ fontFamily: "Manrope", fontSize: 12.5, color: "#6b7280" }}>
          Заявки на перешив с рекламы · WhatsApp приёма:{" "}
          <b data-testid="atelier-wa-number" style={{ color: "#0A0A0A" }}>{meta.wa_number}</b>
          <span style={{ marginLeft: 8, background: "#FEF3C7", border: "1px solid #FCD34D",
                          color: "#92400E", borderRadius: 20, padding: "2px 10px",
                          fontSize: 11, fontWeight: 700 }}>
            номер подключим на следующем шаге
          </span>
        </div>
        <div style={{ flex: 1 }} />
        <button data-testid="atelier-refresh" onClick={load} style={btnSecondary}>
          <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> Обновить
        </button>
        <button data-testid="atelier-create-btn" onClick={() => setShowCreate(true)}
          style={btnPrimary}>
          <Plus size={13} /> Новая заявка
        </button>
      </div>

      {/* Этапы-фильтры */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
        <StageChip label={`Все · ${total}`} active={!filters.status}
          color="#0A0A0A" testId="atelier-stage-all"
          onClick={() => setFilters({ ...filters, status: "" })} />
        {meta.statuses.map(s => (
          <StageChip key={s.key} testId={`atelier-stage-${s.key}`}
            label={`${s.label} · ${data.counts[s.key] || 0}`}
            color={s.color} active={filters.status === s.key}
            onClick={() => setFilters({ ...filters,
              status: filters.status === s.key ? "" : s.key })} />
        ))}
      </div>

      {/* Поиск + фильтр менеджера */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center",
                     marginBottom: 14 }}>
        <form onSubmit={(e) => { e.preventDefault(); load(); }} style={{ position: "relative" }}>
          <Search size={14} style={{ position: "absolute", left: 10, top: "50%",
                                      transform: "translateY(-50%)", color: "#aaa" }} />
          <input data-testid="atelier-search" placeholder="Имя, телефон, изделие, квитанция…"
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            style={{ padding: "8px 10px 8px 32px", borderRadius: 6,
                      border: "1px solid #e5e7eb", fontFamily: "Manrope",
                      fontSize: 13, width: 280 }} />
        </form>
        {canAssign && (
          <select data-testid="atelier-manager-filter" value={filters.manager_id}
            onChange={(e) => setFilters({ ...filters, manager_id: e.target.value })}
            style={selStyle}>
            <option value="">Все приёмщики</option>
            <option value="free">— Без ответственного —</option>
            {managers.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
        )}
        {!canAssign && (
          <select data-testid="atelier-scope-filter" value={filters.manager_id}
            onChange={(e) => setFilters({ ...filters, manager_id: e.target.value })}
            style={selStyle}>
            <option value="">Все заявки ателье</option>
            <option value="mine">Только мои</option>
            <option value="free">Без ответственного</option>
          </select>
        )}
      </div>
      {canAssign && (
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
                       marginBottom: 12, padding: "10px 14px", borderRadius: 8,
                       fontFamily: "Manrope", fontSize: 12.5,
                       background: selected.size ? "rgba(197,160,89,0.08)" : "#fafafa",
                       border: `1px solid ${selected.size ? "#C5A059" : "#e5e7eb"}` }}>
          {selected.size === 0 ? (
            <span style={{ color: "#6b7280" }}>
              Кликните по колонке-галочке слева — появится массовое назначение приёмщика
            </span>
          ) : (
            <>
              <b data-testid="atelier-selected-count" style={{ color: "#7c5a1e" }}>
                Выбрано: {selected.size}
              </b>
              <button onClick={toggleAll} style={ghostBtn}>
                {allSelected ? "Снять всё" : `Выделить всё (${data.items.length})`}
              </button>
              <div style={{ flex: 1 }} />
              <button data-testid="atelier-bulk-assign-btn" onClick={() => setAssignOpen(true)}
                style={{ ...btnPrimary, padding: "8px 18px" }}>
                <UserPlus size={13} /> Назначить приёмщика ({selected.size})
              </button>
            </>
          )}
        </div>
      )}

      {/* Таблица заявок */}
      {loading ? (
        <div style={{ padding: 40, textAlign: "center", color: "#999",
                       fontFamily: "Manrope" }}>Загрузка…</div>
      ) : data.items.length === 0 ? (
        <div data-testid="atelier-empty"
          style={{ padding: 40, textAlign: "center", color: "#94a3b8",
                    fontFamily: "Manrope", background: "white",
                    border: "1px solid #e5e7eb", borderRadius: 10 }}>
          Заявок нет. Нажмите «Новая заявка» или дождитесь обращений с рекламы.
        </div>
      ) : (
        <div style={{ background: "white", border: "1px solid #e5e7eb",
                       borderRadius: 10, overflow: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 1040 }}>
            <thead>
              <tr style={{ background: "#fafafa", borderBottom: "1px solid #e5e7eb" }}>
                {canAssign && (
                  <th style={{ ...th, width: 56, padding: 0 }}>
                    <div onClick={toggleAll} data-testid="atelier-select-all-cell"
                      style={{ padding: "14px 16px", cursor: "pointer", display: "flex",
                                justifyContent: "center" }}>
                      <input type="checkbox" checked={allSelected} onChange={toggleAll}
                        onClick={(e) => e.stopPropagation()}
                        style={{ width: 17, height: 17 }} />
                    </div>
                  </th>
                )}
                <th style={th}>Клиент</th><th style={th}>Изделие / работа</th>
                <th style={th}>Этап</th><th style={th}>Цена / предоплата</th>
                <th style={th}>Визит</th><th style={th}>Примерка</th><th style={th}>Срок</th>
                <th style={th}>Приёмщик</th><th style={th}>Квитанция</th><th style={th} />
              </tr>
            </thead>
            <tbody>
              {data.items.map(o => {
                const isSel = selected.has(o.id);
                return (
                  <tr key={o.id} data-testid={`atelier-row-${o.id}`}
                    onClick={() => setOpenId(o.id)}
                    style={{ borderBottom: "1px solid #f1f5f9", cursor: "pointer",
                              background: isSel ? "rgba(197,160,89,0.16)" : "white" }}>
                    {canAssign && (
                      <td data-testid={`atelier-select-cell-${o.id}`}
                        onClick={(e) => { e.stopPropagation(); toggleOne(o.id); }}
                        style={{ width: 56, padding: 0 }}>
                        <div style={{ padding: "16px", display: "flex",
                                       justifyContent: "center", cursor: "pointer" }}>
                          <input type="checkbox" checked={isSel}
                            onChange={() => toggleOne(o.id)}
                            onClick={(e) => e.stopPropagation()}
                            style={{ width: 17, height: 17 }} />
                        </div>
                      </td>
                    )}
                    <td style={td}>
                      <div style={{ fontWeight: 600 }}>{o.name || "Без имени"}</div>
                      <div style={{ display: "flex", gap: 8, alignItems: "center",
                                     marginTop: 3 }}>
                        <a href={`tel:${o.phone}`} onClick={(e) => e.stopPropagation()}
                          style={{ fontSize: 12, color: "#6b7280" }}>{o.phone}</a>
                        <a href={waLink(o.whatsapp || o.phone)} target="_blank"
                          rel="noreferrer" onClick={(e) => e.stopPropagation()}
                          title="Написать в WhatsApp"
                          style={{ color: "#25D366", display: "inline-flex" }}>
                          <MessageCircle size={13} />
                        </a>
                      </div>
                    </td>
                    <td style={td}>
                      <div>{o.item || "—"}</div>
                      <div style={{ fontSize: 11.5, color: "#6b7280" }}>{o.work_type_label}</div>
                    </td>
                    <td style={td}>
                      <span style={{ background: o.status_color + "22",
                                      border: `1px solid ${o.status_color}`,
                                      color: "#0A0A0A", borderRadius: 20,
                                      padding: "3px 10px", fontSize: 11, fontWeight: 700 }}>
                        {o.status_label}
                      </span>
                    </td>
                    <td style={td}>
                      <div>{money(o.price)}</div>
                      <div style={{ fontSize: 11.5,
                                     color: o.prepaid_amount ? "#059669" : "#9ca3af" }}>
                        {o.prepaid_amount ? `предоплата ${money(o.prepaid_amount)}`
                                           : "предоплаты нет"}
                      </div>
                    </td>
                    <td style={{ ...td, fontSize: 12 }}>{fmtDateTime(o.visit_at)}</td>
                    <td style={{ ...td, fontSize: 12 }}>{fmtDateTime(o.fitting_at)}</td>
                    <td style={{ ...td, fontSize: 12 }}>{fmtDate(o.deadline_at)}</td>
                    <td style={{ ...td, fontSize: 12 }}>{o.manager_name || "—"}</td>
                    <td style={{ ...td, fontSize: 12 }}>{o.receipt_no || "—"}</td>
                    <td style={{ ...td, width: 40 }}><ChevronRight size={16} color="#bbb" /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showCreate && <OrderForm meta={meta} managers={managers} canAssign={canAssign}
        onClose={(saved) => { setShowCreate(false); if (saved) load(); }} />}
      {openId && <OrderDetail orderId={openId} meta={meta} managers={managers}
        canAssign={canAssign}
        onClose={(changed) => { setOpenId(null); if (changed) load(); }} />}
      {assignOpen && <AtelierAssignModal orderIds={Array.from(selected)}
        managers={managers}
        onClose={(done) => { setAssignOpen(false); if (done) load(); }} />}
    </div>
  );
}

const StageChip = ({ label, color, active, onClick, testId }) => (
  <button data-testid={testId} onClick={onClick}
    style={{ background: active ? color : "white", color: active ? "white" : "#0A0A0A",
              border: `1px solid ${active ? color : "#e5e7eb"}`, borderRadius: 20,
              padding: "6px 14px", cursor: "pointer", fontFamily: "Manrope",
              fontSize: 12, fontWeight: 600, transition: "all .15s" }}>
    {label}
  </button>
);

/* ---------- Новая заявка ---------- */
function OrderForm({ meta, managers, canAssign, onClose }) {
  const [f, setF] = useState({
    name: "", phone: "", item: "", work_type: "resize", scope: "",
    price: "", prepaid_amount: "", receipt_no: "", master: "",
    visit_at: "", fitting_at: "", deadline_at: "", manager_id: meta.user_id, comment: "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const set = (k, v) => setF({ ...f, [k]: v });

  const submit = async () => {
    if (!f.phone.trim()) { setError("Укажите телефон клиента"); return; }
    setSaving(true); setError("");
    try {
      await axios.post(`${API}/admin/atelier/orders`, {
        ...f,
        price: f.price ? Number(f.price) : null,
        prepaid_amount: f.prepaid_amount ? Number(f.prepaid_amount) : null,
        visit_at: toIso(f.visit_at),
        fitting_at: toIso(f.fitting_at),
        deadline_at: f.deadline_at || null,
      }, { withCredentials: true });
      onClose(true);
    } catch (e) {
      setError(e.response?.data?.detail || "Не удалось сохранить");
    } finally { setSaving(false); }
  };

  return (
    <Modal title="Новая заявка на перешив" testId="atelier-create-modal"
      onClose={() => onClose(false)}>
      <Row>
        <Field label="Имя клиента"><Inp testId="atelier-f-name" value={f.name}
          onChange={(v) => set("name", v)} placeholder="Айгерим" /></Field>
        <Field label="Телефон *"><Inp testId="atelier-f-phone" value={f.phone}
          onChange={(v) => set("phone", v)} placeholder="+7 708 ..." /></Field>
      </Row>
      <Row>
        <Field label="Изделие"><Inp testId="atelier-f-item" value={f.item}
          onChange={(v) => set("item", v)} placeholder="Шуба норка, чёрная" /></Field>
        <Field label="Тип работы">
          <select data-testid="atelier-f-worktype" value={f.work_type}
            onChange={(e) => set("work_type", e.target.value)} style={inpStyle}>
            {meta.work_types.map(w => <option key={w.key} value={w.key}>{w.label}</option>)}
          </select>
        </Field>
      </Row>
      <Field label="Что нужно сделать (объём работ)">
        <textarea data-testid="atelier-f-scope" value={f.scope}
          onChange={(e) => set("scope", e.target.value)}
          placeholder="Ушить по фигуре, укоротить рукава на 4 см, заменить подкладку"
          style={{ ...inpStyle, minHeight: 70, resize: "vertical" }} />
      </Field>
      <Row>
        <Field label="Цена работ, ₸"><Inp testId="atelier-f-price" type="number"
          value={f.price} onChange={(v) => set("price", v)} placeholder="60000" /></Field>
        <Field label="Предоплата, ₸"><Inp testId="atelier-f-prepaid" type="number"
          value={f.prepaid_amount} onChange={(v) => set("prepaid_amount", v)}
          placeholder="30000" /></Field>
      </Row>
      <Row>
        <Field label="Визит в ателье"><Inp testId="atelier-f-visit" type="datetime-local"
          value={f.visit_at} onChange={(v) => set("visit_at", v)} /></Field>
        <Field label="Первая примерка"><Inp testId="atelier-f-fitting" type="datetime-local"
          value={f.fitting_at} onChange={(v) => set("fitting_at", v)} /></Field>
      </Row>
      <Row>
        <Field label="Срок готовности"><Inp testId="atelier-f-deadline" type="date"
          value={f.deadline_at} onChange={(v) => set("deadline_at", v)} /></Field>
      </Row>
      <Row>
        <Field label="Квитанция №"><Inp testId="atelier-f-receipt" value={f.receipt_no}
          onChange={(v) => set("receipt_no", v)} /></Field>
        <Field label="Мастер"><Inp testId="atelier-f-master" value={f.master}
          onChange={(v) => set("master", v)} /></Field>
      </Row>
      {canAssign && (
        <Field label="Приёмщик (ответственный)">
          <select data-testid="atelier-f-manager" value={f.manager_id}
            onChange={(e) => set("manager_id", e.target.value)} style={inpStyle}>
            {managers.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
        </Field>
      )}
      {error && <ErrorBox>{error}</ErrorBox>}
      <Actions>
        <button onClick={() => onClose(false)} style={btnSecondary}>Отмена</button>
        <button data-testid="atelier-create-submit" onClick={submit} disabled={saving}
          style={btnPrimary}>{saving ? "Сохраняем…" : "Создать заявку"}</button>
      </Actions>
    </Modal>
  );
}

/* ---------- Карточка заявки ---------- */
function OrderDetail({ orderId, meta, managers, canAssign, onClose }) {
  const [order, setOrder] = useState(null);
  const [edit, setEdit] = useState({});
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [changed, setChanged] = useState(false);
  const [error, setError] = useState("");
  const [tasks, setTasks] = useState([]);
  const [taskForm, setTaskForm] = useState({ title: "", due_at: "", assigned_manager_id: "" });
  const [fittingsShown, setFittingsShown] = useState(1);
  const taskManagers = (managers && managers.length > 1) ? managers : (meta.managers || managers || []);

  const loadTasks = async () => {
    try {
      const r = await axios.get(`${API}/admin/atelier/orders/${orderId}/tasks`,
        { withCredentials: true });
      setTasks(r.data.items || []);
    } catch { /* noop */ }
  };

  const addTask = async () => {
    if (!taskForm.title.trim()) return;
    setBusy(true); setError("");
    try {
      await axios.post(`${API}/admin/atelier/orders/${orderId}/tasks`, {
        title: taskForm.title,
        due_at: taskForm.due_at ? new Date(taskForm.due_at).toISOString() : null,
        assigned_manager_id: taskForm.assigned_manager_id || null,
      }, { withCredentials: true });
      setTaskForm({ title: "", due_at: "", assigned_manager_id: "" });
      await loadTasks(); setChanged(true);
    } catch (e) {
      setError(e.response?.data?.detail || "Не удалось поставить задачу");
    } finally { setBusy(false); }
  };

  const patchTask = async (id, payload) => {
    setBusy(true);
    try {
      await axios.patch(`${API}/admin/atelier/tasks/${id}`, payload,
        { withCredentials: true });
      await loadTasks();
    } catch (e) {
      setError(e.response?.data?.detail || "Не удалось обновить задачу");
    } finally { setBusy(false); }
  };

  const delTask = async (id) => {
    setBusy(true);
    try {
      await axios.delete(`${API}/admin/atelier/tasks/${id}`, { withCredentials: true });
      await loadTasks();
    } finally { setBusy(false); }
  };

  const load = async () => {
    const r = await axios.get(`${API}/admin/atelier/orders/${orderId}`,
      { withCredentials: true });
    setOrder(r.data);
    setFittingsShown(Math.max(1, [2, 3, 4].filter(n => r.data[`fitting_${n}_at`]).length + 1));
    setEdit({
      item: r.data.item, work_type: r.data.work_type, scope: r.data.scope,
      price: r.data.price ?? "", prepaid_amount: r.data.prepaid_amount ?? "",
      receipt_no: r.data.receipt_no, master: r.data.master,
      visit_at: toLocalInput(r.data.visit_at),
      fitting_at: toLocalInput(r.data.fitting_at),
      fitting_2_at: toLocalInput(r.data.fitting_2_at),
      fitting_3_at: toLocalInput(r.data.fitting_3_at),
      fitting_4_at: toLocalInput(r.data.fitting_4_at),
      deadline_at: r.data.deadline_at ? r.data.deadline_at.slice(0, 10) : "",
      manager_id: r.data.manager_id || "",
    });
  };
  useEffect(() => { load().catch(console.error); loadTasks(); /* eslint-disable-next-line */ }, [orderId]);

  const patch = async (payload) => {
    setBusy(true); setError("");
    try {
      const r = await axios.patch(`${API}/admin/atelier/orders/${orderId}`,
        payload, { withCredentials: true });
      setOrder(r.data);
      setChanged(true);
      await loadTasks();   // автозадачи по примеркам появляются сразу
    } catch (e) {
      setError(e.response?.data?.detail || "Не удалось сохранить");
    } finally { setBusy(false); }
  };

  const saveFields = (override) => patch({
    ...edit,
    price: edit.price === "" ? null : Number(edit.price),
    prepaid_amount: edit.prepaid_amount === "" ? null : Number(edit.prepaid_amount),
    visit_at: toIso(edit.visit_at),
    fitting_at: toIso(edit.fitting_at),
    fitting_2_at: toIso(edit.fitting_2_at),
    fitting_3_at: toIso(edit.fitting_3_at),
    fitting_4_at: toIso(edit.fitting_4_at),
    deadline_at: edit.deadline_at || null,
    manager_id: edit.manager_id,
    ...(override || {}),
  });

  const onMedia = (updated) => { setOrder(updated); setChanged(true); };

  const addNote = async () => {
    if (!note.trim()) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/atelier/orders/${orderId}/notes`,
        { text: note }, { withCredentials: true });
      setOrder(r.data); setNote(""); setChanged(true);
    } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm("Удалить заявку безвозвратно?")) return;
    await axios.delete(`${API}/admin/atelier/orders/${orderId}`, { withCredentials: true });
    onClose(true);
  };

  if (!order) return null;

  return (
    <Modal title={`Заявка · ${order.name || order.phone}`} wide
      testId="atelier-detail" onClose={() => onClose(changed)}>
      {/* Этапы */}
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 16 }}>
        {meta.statuses.map(s => (
          <button key={s.key} data-testid={`atelier-status-${s.key}`}
            onClick={() => patch({ status: s.key })} disabled={busy}
            style={{ background: order.status === s.key ? s.color : "white",
                      color: order.status === s.key ? "white" : "#374151",
                      border: `1px solid ${order.status === s.key ? s.color : "#e5e7eb"}`,
                      borderRadius: 20, padding: "6px 12px", cursor: "pointer",
                      fontSize: 11.5, fontWeight: 600, fontFamily: "Manrope" }}>
            {s.label}
          </button>
        ))}
      </div>

      <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
        <a href={`tel:${order.phone}`} style={btnSecondary}><Phone size={13} /> Позвонить</a>
        <a href={waLink(order.whatsapp || order.phone)} target="_blank" rel="noreferrer"
          style={{ ...btnSecondary, color: "#059669" }}>
          <MessageCircle size={13} /> WhatsApp
        </a>
        <div style={{ flex: 1 }} />
        {canAssign && (
          <button data-testid="atelier-delete" onClick={remove}
            style={{ ...btnSecondary, color: "#b91c1c", borderColor: "#fecaca" }}>
            <Trash2 size={13} /> Удалить
          </button>
        )}
      </div>

      <Row>
        <Field label="Изделие"><Inp testId="atelier-d-item" value={edit.item}
          onChange={(v) => setEdit({ ...edit, item: v })} /></Field>
        <Field label="Тип работы">
          <select data-testid="atelier-d-worktype" value={edit.work_type}
            onChange={(e) => setEdit({ ...edit, work_type: e.target.value })}
            style={inpStyle}>
            {meta.work_types.map(w => <option key={w.key} value={w.key}>{w.label}</option>)}
          </select>
        </Field>
      </Row>
      <Field label="Объём работ">
        <textarea data-testid="atelier-d-scope" value={edit.scope}
          onChange={(e) => setEdit({ ...edit, scope: e.target.value })}
          style={{ ...inpStyle, minHeight: 70, resize: "vertical" }} />
      </Field>
      <Row>
        <Field label="Цена работ, ₸"><Inp testId="atelier-d-price" type="number"
          value={edit.price} onChange={(v) => setEdit({ ...edit, price: v })} /></Field>
        <Field label="Предоплата, ₸"><Inp testId="atelier-d-prepaid" type="number"
          value={edit.prepaid_amount}
          onChange={(v) => setEdit({ ...edit, prepaid_amount: v })} /></Field>
      </Row>
      <Row>
        <Field label="Визит в ателье"><Inp testId="atelier-d-visit" type="datetime-local"
          value={edit.visit_at}
          onChange={(v) => setEdit({ ...edit, visit_at: v })} /></Field>
        <Field label="1-я примерка"><Inp testId="atelier-d-fitting" type="datetime-local"
          value={edit.fitting_at}
          onChange={(v) => setEdit({ ...edit, fitting_at: v })} /></Field>
      </Row>
      <Row>
        {[2, 3, 4].filter(n => n <= fittingsShown).map(n => (
          <Field key={n} label={`${n}-я примерка`}>
            <Inp testId={`atelier-d-fitting-${n}`} type="datetime-local"
              value={edit[`fitting_${n}_at`] || ""}
              onChange={(v) => setEdit({ ...edit, [`fitting_${n}_at`]: v })} />
          </Field>
        ))}
      </Row>
      {fittingsShown < 4 && (
        <button data-testid="atelier-add-fitting" onClick={() => setFittingsShown(fittingsShown + 1)}
          style={{ ...btnSecondary, marginBottom: 10 }}>
          + Добавить {fittingsShown + 1}-ю примерку
        </button>
      )}
      <div style={{ fontFamily: "Manrope", fontSize: 11, color: "#9ca3af", marginBottom: 10 }}>
        После сохранения по каждой дате примерки автоматически ставится задача связаться с клиентом и напомнить о записи.
      </div>
      <Row>
        <Field label="Срок готовности"><Inp testId="atelier-d-deadline" type="date"
          value={edit.deadline_at}
          onChange={(v) => setEdit({ ...edit, deadline_at: v })} /></Field>
      </Row>
      <Row>
        <Field label="Квитанция №"><Inp testId="atelier-d-receipt" value={edit.receipt_no}
          onChange={(v) => setEdit({ ...edit, receipt_no: v })} /></Field>
        <Field label="Мастер"><Inp testId="atelier-d-master" value={edit.master}
          onChange={(v) => setEdit({ ...edit, master: v })} /></Field>
      </Row>
      {canAssign && (
        <Field label="Приёмщик">
          <select data-testid="atelier-d-manager" value={edit.manager_id}
            onChange={(e) => setEdit({ ...edit, manager_id: e.target.value })}
            style={inpStyle}>
            <option value="">— без ответственного —</option>
            {managers.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
        </Field>
      )}
      {!canAssign && !order.manager_id && (
        <div data-testid="atelier-claim-box"
          style={{ background: "#FDFBF7", border: "1px dashed #C5A059", borderRadius: 8,
                    padding: "10px 12px", display: "flex", alignItems: "center",
                    justifyContent: "space-between", gap: 10, marginTop: 4 }}>
          <span style={{ fontFamily: "Manrope", fontSize: 12, color: "#6b7280" }}>
            Заявка свободна — ответьте клиенту или нажмите «Взять себе»
          </span>
          <button data-testid="atelier-claim-btn" disabled={busy}
            onClick={() => saveFields({ manager_id: meta.user_id })}
            style={{ ...btnPrimary, padding: "8px 14px" }}>
            Взять себе
          </button>
        </div>
      )}
      {error && <ErrorBox>{error}</ErrorBox>}
      <Actions>
        <button data-testid="atelier-save" onClick={() => saveFields()} disabled={busy}
          style={btnPrimary}><Check size={13} /> Сохранить</button>
      </Actions>

      {/* Файлы по заявке — только для сотрудников */}
      <div style={{ marginTop: 20, borderTop: "1px solid #f1f5f9", paddingTop: 14 }}>
        <div style={{ fontFamily: "Manrope", fontSize: 12, fontWeight: 700, color: "#374151" }}>
          Файлы по заявке
        </div>
        <div style={{ fontFamily: "Manrope", fontSize: 11, color: "#9ca3af", marginTop: 2 }}>
          Видно только в админке — клиенту эти файлы не показываются.
        </div>
        <AtelierMedia orderId={orderId} attachments={order.attachments} kind="before"
          label="Изделие ДО перешива" accept="image/*"
          hint="Фото изделия, полученного от клиента." onUpdate={onMedia} />
        <AtelierMedia orderId={orderId} attachments={order.attachments} kind="contract"
          label="Договор с клиентом" accept="application/pdf,image/*"
          hint="Подписанный договор с суммой — PDF или фото, можно скачать." onUpdate={onMedia} />
        <AtelierVideoSection orderId={orderId} attachments={order.attachments} onUpdate={onMedia} />
        <AtelierMedia orderId={orderId} attachments={order.attachments} kind="after"
          label="Изделие ПОСЛЕ перешива" accept="image/*"
          hint="Готовый результат для отчётности и портфолио." onUpdate={onMedia} />
      </div>

      {/* Задачи по заявке */}
      <div style={{ marginTop: 20, borderTop: "1px solid #f1f5f9", paddingTop: 14 }}>
        <div style={{ fontFamily: "Manrope", fontSize: 12, fontWeight: 700,
                       color: "#374151", marginBottom: 8 }}>
          Задачи по заявке
        </div>
        {tasks.length === 0 && (
          <div style={{ fontSize: 12, color: "#9ca3af", marginBottom: 8 }}>
            Задач нет. Поставьте следующий шаг — напомнить о примерке, забрать изделие, позвонить мастеру.
          </div>
        )}
        {tasks.map(t => (
          <div key={t.id} data-testid={`atelier-task-${t.id}`}
            style={{ display: "flex", alignItems: "flex-start", gap: 8,
                      background: t.status === "done" ? "#f0fdf4" : "#f9fafb",
                      borderRadius: 8, padding: "8px 12px", marginBottom: 6 }}>
            <input type="checkbox" checked={t.status === "done"} disabled={busy}
              data-testid={`atelier-task-done-${t.id}`}
              onChange={() => patchTask(t.id, { status: t.status === "done" ? "pending" : "done" })}
              style={{ marginTop: 3, width: 15, height: 15, cursor: "pointer" }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 12.5, color: "#0A0A0A",
                             textDecoration: t.status === "done" ? "line-through" : "none" }}>
                {t.title}
              </div>
              <div style={{ fontSize: 11, color: t.is_overdue ? "#dc2626" : "#9ca3af",
                             marginTop: 2 }}>
                {t.due_at ? `до ${fmtDateTime(t.due_at)}` : "без срока"}
                {t.assigned_manager_name ? ` · ${t.assigned_manager_name}` : ""}
                {t.comment ? ` · ${t.comment}` : ""}
              </div>
            </div>
            <button data-testid={`atelier-task-del-${t.id}`} disabled={busy}
              onClick={() => delTask(t.id)}
              style={{ background: "none", border: "none", cursor: "pointer",
                        color: "#c4c4c4", fontSize: 14 }}>×</button>
          </div>
        ))}
        <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
          <Inp testId="atelier-task-title" value={taskForm.title}
            onChange={(v) => setTaskForm({ ...taskForm, title: v })}
            placeholder="Что сделать: напомнить о примерке" />
          <input data-testid="atelier-task-due" type="datetime-local"
            value={taskForm.due_at}
            onChange={(e) => setTaskForm({ ...taskForm, due_at: e.target.value })}
            style={{ border: "1px solid #e5e7eb", borderRadius: 6, padding: "8px 10px",
                      fontFamily: "Manrope", fontSize: 12.5 }} />
          <select data-testid="atelier-task-manager" value={taskForm.assigned_manager_id}
            onChange={(e) => setTaskForm({ ...taskForm, assigned_manager_id: e.target.value })}
            style={{ border: "1px solid #e5e7eb", borderRadius: 6, padding: "8px 10px",
                      fontFamily: "Manrope", fontSize: 12.5 }}>
            <option value="">На себя</option>
            {taskManagers.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
          <button data-testid="atelier-task-add" onClick={addTask} disabled={busy}
            style={btnSecondary}>Поставить задачу</button>
        </div>
      </div>

      {/* Переписка WhatsApp с номера ателье */}
      <div style={{ marginTop: 20 }}>
        <WhatsAppChat
          channel="atelier"
          clientId={orderId}
          client={{ name: order.name, city: "" }}
          managerName={order.manager_name}
        />
      </div>

      {/* Комментарии */}
      <div style={{ marginTop: 20, borderTop: "1px solid #f1f5f9", paddingTop: 14 }}>
        <div style={{ fontFamily: "Manrope", fontSize: 12, fontWeight: 700,
                       color: "#374151", marginBottom: 8 }}>
          Комментарии по заявке
        </div>
        {(order.notes || []).map(n => (
          <div key={n.id} style={{ background: "#f9fafb", borderRadius: 8,
                                    padding: "8px 12px", marginBottom: 6, fontSize: 12.5 }}>
            <div style={{ color: "#0A0A0A" }}>{n.text}</div>
            <div style={{ color: "#9ca3af", fontSize: 11, marginTop: 2 }}>
              {n.manager_name} · {fmtDateTime(n.created_at)}
            </div>
          </div>
        ))}
        <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
          <Inp testId="atelier-note-input" value={note} onChange={setNote}
            placeholder="Например: клиент принесёт шубу в субботу" />
          <button data-testid="atelier-note-add" onClick={addNote} disabled={busy}
            style={btnSecondary}>Добавить</button>
        </div>
      </div>

      {/* История этапов */}
      {(order.history || []).length > 0 && (
        <div style={{ marginTop: 18, borderTop: "1px solid #f1f5f9", paddingTop: 14 }}>
          <div style={{ fontFamily: "Manrope", fontSize: 12, fontWeight: 700,
                         color: "#374151", marginBottom: 8 }}>История этапов</div>
          {order.history.slice().reverse().map((h, i) => (
            <div key={i} style={{ fontSize: 12, color: "#6b7280", padding: "3px 0" }}>
              {fmtDateTime(h.at)} · {meta.statuses.find(s => s.key === h.from)?.label || "—"}
              {" → "}
              <b style={{ color: "#0A0A0A" }}>
                {meta.statuses.find(s => s.key === h.to)?.label || h.to}
              </b>
              {h.by_name ? ` (${h.by_name})` : ""}
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}

/* ---------- Массовое назначение приёмщика ---------- */
function AtelierAssignModal({ orderIds, managers, onClose }) {
  const [mode, setMode] = useState("single");
  const [singleId, setSingleId] = useState(managers[0]?.id || "");
  const [picked, setPicked] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  const toggle = (id) => {
    const next = new Set(picked);
    if (next.has(id)) next.delete(id); else next.add(id);
    setPicked(next);
  };

  const submit = async () => {
    const manager_ids = mode === "single" ? (singleId ? [singleId] : []) : Array.from(picked);
    if (!manager_ids.length) { setError("Выберите менеджеров"); return; }
    setBusy(true); setError("");
    try {
      const r = await axios.post(`${API}/admin/atelier/orders/bulk-assign`,
        { order_ids: orderIds, manager_ids }, { withCredentials: true });
      setResult(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Не удалось назначить");
    } finally { setBusy(false); }
  };

  return (
    <Modal title={`Назначить приёмщика · ${orderIds.length} заявок`}
      testId="atelier-assign-modal" onClose={() => onClose(!!result)}>
      {result ? (
        <>
          <div style={{ background: "#ECFDF5", border: "1px solid #A7F3D0",
                         color: "#065F46", borderRadius: 8, padding: 14, fontSize: 13 }}>
            Назначено заявок: <b>{result.assigned}</b>
            <ul style={{ margin: "8px 0 0 16px" }}>
              {Object.entries(result.per_manager || {}).map(([n, c]) => (
                <li key={n}>{n} — {c}</li>
              ))}
            </ul>
          </div>
          <Actions>
            <button data-testid="atelier-assign-done" onClick={() => onClose(true)}
              style={btnPrimary}>Готово</button>
          </Actions>
        </>
      ) : (
        <>
          <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
            <button data-testid="atelier-assign-single" onClick={() => setMode("single")}
              style={{ ...chip, ...(mode === "single" ? chipActive : {}) }}>
              Одному приёмщику
            </button>
            <button data-testid="atelier-assign-rr" onClick={() => setMode("round_robin")}
              style={{ ...chip, ...(mode === "round_robin" ? chipActive : {}) }}>
              Распределить по кругу
            </button>
          </div>
          {mode === "single" ? (
            <Field label="Приёмщик">
              <select data-testid="atelier-assign-select" value={singleId}
                onChange={(e) => setSingleId(e.target.value)} style={inpStyle}>
                <option value="">— выберите —</option>
                {managers.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
            </Field>
          ) : (
            <div style={{ border: "1px solid #e5e7eb", borderRadius: 8,
                           maxHeight: 220, overflowY: "auto" }}>
              {managers.map(m => (
                <div key={m.id} onClick={() => toggle(m.id)}
                  data-testid={`atelier-assign-pick-${m.id}`}
                  style={{ display: "flex", gap: 10, alignItems: "center",
                            padding: "10px 14px", cursor: "pointer",
                            borderBottom: "1px solid #f5f5f5", fontSize: 13,
                            background: picked.has(m.id) ? "rgba(197,160,89,0.10)" : "white" }}>
                  <input type="checkbox" checked={picked.has(m.id)}
                    onChange={() => toggle(m.id)} onClick={(e) => e.stopPropagation()}
                    style={{ width: 16, height: 16 }} />
                  {m.name}
                </div>
              ))}
            </div>
          )}
          {error && <ErrorBox>{error}</ErrorBox>}
          <Actions>
            <button onClick={() => onClose(false)} style={btnSecondary}>Отмена</button>
            <button data-testid="atelier-assign-submit" onClick={submit} disabled={busy}
              style={btnPrimary}>{busy ? "Назначаем…" : "Назначить"}</button>
          </Actions>
        </>
      )}
    </Modal>
  );
}

/* ---------- Мелкие UI-элементы ---------- */
const Modal = ({ title, children, onClose, wide, testId }) => (
  <div onClick={onClose}
    style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.5)", zIndex: 140,
              display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
    <div data-testid={testId} onClick={(e) => e.stopPropagation()}
      style={{ background: "white", borderRadius: 12, padding: 22, width: "100%",
                maxWidth: wide ? 760 : 560, maxHeight: "92vh", overflowY: "auto",
                fontFamily: "Manrope" }}>
      <div style={{ display: "flex", justifyContent: "space-between",
                     alignItems: "center", marginBottom: 16 }}>
        <div style={{ fontFamily: "Cormorant Garamond", fontSize: 24, color: "#0A0A0A" }}>
          {title}
        </div>
        <button onClick={onClose}
          style={{ background: "none", border: "none", cursor: "pointer" }}>
          <X size={16} />
        </button>
      </div>
      {children}
    </div>
  </div>
);
const Row = ({ children }) => (
  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>{children}</div>
);
const Field = ({ label, children }) => (
  <div style={{ marginBottom: 12 }}>
    <label style={{ display: "block", fontSize: 11, textTransform: "uppercase",
                     letterSpacing: 0.5, color: "#8a8a89", marginBottom: 4,
                     fontWeight: 700 }}>{label}</label>
    {children}
  </div>
);
const Inp = ({ value, onChange, testId, ...rest }) => (
  <input data-testid={testId} value={value ?? ""}
    onChange={(e) => onChange(e.target.value)} {...rest} style={inpStyle} />
);
const ErrorBox = ({ children }) => (
  <div data-testid="atelier-error"
    style={{ background: "#FEF2F2", border: "1px solid #FECACA", color: "#991B1B",
              borderRadius: 6, padding: "8px 12px", fontSize: 12.5, marginTop: 10 }}>
    {children}
  </div>
);
const Actions = ({ children }) => (
  <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
    {children}
  </div>
);

const th = { textAlign: "left", padding: "10px 14px", fontFamily: "Manrope",
             fontSize: 10, color: "#888", letterSpacing: "0.1em",
             textTransform: "uppercase", fontWeight: 700 };
const td = { padding: "10px 14px", fontFamily: "Manrope", fontSize: 13, color: "#0A0A0A" };
const inpStyle = { width: "100%", padding: "9px 11px", border: "1px solid #e5e7eb",
                   borderRadius: 6, fontFamily: "Manrope", fontSize: 13,
                   color: "#0A0A0A", background: "white", boxSizing: "border-box" };
const selStyle = { padding: "8px 10px", borderRadius: 6, border: "1px solid #e5e7eb",
                   fontFamily: "Manrope", fontSize: 13, background: "white" };
const btnPrimary = { background: "linear-gradient(135deg,#C5A059,#D4B878)", color: "#0A0A0A",
                     border: "none", borderRadius: 6, padding: "9px 16px", cursor: "pointer",
                     fontFamily: "Manrope", fontSize: 12.5, fontWeight: 700,
                     display: "inline-flex", alignItems: "center", gap: 6 };
const btnSecondary = { background: "white", color: "#374151", border: "1px solid #e5e7eb",
                       borderRadius: 6, padding: "8px 14px", cursor: "pointer",
                       fontFamily: "Manrope", fontSize: 12.5, fontWeight: 600,
                       display: "inline-flex", alignItems: "center", gap: 6,
                       textDecoration: "none" };
const ghostBtn = { background: "transparent", border: "1px solid #e5e7eb",
                   padding: "4px 10px", borderRadius: 6, cursor: "pointer",
                   fontSize: 11, color: "#6b7280" };
const chip = { padding: "8px 14px", background: "white", border: "1px solid #e5e7eb",
               borderRadius: 8, cursor: "pointer", fontSize: 12.5, fontWeight: 600 };
const chipActive = { border: "1px solid #C5A059", background: "rgba(197,160,89,0.12)",
                     color: "#7c5a1e" };
